import type { MetricDefinition } from "@/grid/core/types";
import {
  defaultMetricConfig,
  indexMetricConfigs,
  type TargetMetricConfig,
  type TargetStoragePeriod,
} from "@/grid/core/target-policy";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { upsertTargetMetricConfig } from "@/targets/api/target-config-api";
import { cn } from "@/lib/cn";
import { useCallback, useMemo, useState } from "react";

interface TargetInferencePanelProps {
  departmentId: string;
  metrics: MetricDefinition[];
  configs: TargetMetricConfig[];
  onSaved?: () => void;
  title?: string;
  description?: string;
  embedded?: boolean;
}

export function TargetInferencePanel({
  departmentId,
  metrics,
  configs,
  onSaved,
  title = "Roll-up rules",
  description = "Choose where each metric is set vs inferred. Turn off inference to enter explicit month or quarter targets below.",
  embedded = false,
}: TargetInferencePanelProps) {
  const configByMetric = useMemo(
    () => indexMetricConfigs(departmentId, metrics, configs),
    [configs, departmentId, metrics],
  );

  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const saveConfig = useCallback(
    async (next: TargetMetricConfig) => {
      setSaveState("saving");
      try {
        await upsertTargetMetricConfig(next);
        setSaveState("saved");
        onSaved?.();
      } catch {
        setSaveState("error");
      }
    },
    [onSaved],
  );

  const updateConfig = useCallback(
    (metric: MetricDefinition, patch: Partial<TargetMetricConfig>) => {
      const current =
        configByMetric.get(metric.targetMetric) ?? defaultMetricConfig(departmentId, metric);
      const next: TargetMetricConfig = { ...current, ...patch };
      void saveConfig(next);
    },
    [configByMetric, departmentId, saveConfig],
  );

  return (
    <section
      className={cn(
        embedded
          ? "overflow-hidden rounded-2xl ring-1 ring-[var(--color-border)]"
          : "rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4",
        embedded && "p-4",
      )}
    >
      {title || description ? (
        <div className="mb-3">
          {title ? <h2 className="text-sm font-medium text-[var(--color-text)]">{title}</h2> : null}
          {description ? (
            <p className="text-xs text-[var(--color-text-muted)]">{description}</p>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead className="bg-[var(--color-surface-muted)]">
            <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-text-muted)]">
              <th className="px-2 py-2 font-medium">Metric</th>
              <th className="px-2 py-2 font-medium">Set targets at</th>
              <th className="px-2 py-2 font-medium">Infer MTD from weekly</th>
              <th className="px-2 py-2 font-medium">Infer QTD from weekly</th>
              <th className="px-2 py-2 font-medium">Infer QTD from monthly</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => {
              const config =
                configByMetric.get(metric.targetMetric) ??
                defaultMetricConfig(departmentId, metric);
              const isWeekly = config.setAt === "week_default";

              return (
                <tr
                  key={metric.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]/50"
                >
                  <td className="px-2 py-2 font-medium text-[var(--color-text)]">
                    {metric.shortLabel ?? metric.label}
                  </td>
                  <td className="px-2 py-2">
                    <NativeSelect
                      value={config.setAt}
                      onChange={(event) =>
                        updateConfig(metric, {
                          setAt: event.target.value as TargetStoragePeriod,
                          inferMtdFromWeekly:
                            event.target.value === "week_default"
                              ? config.inferMtdFromWeekly
                              : false,
                          inferQtdFromWeekly:
                            event.target.value === "week_default"
                              ? config.inferQtdFromWeekly
                              : false,
                          inferQtdFromMonthly:
                            event.target.value === "month" ? config.inferQtdFromMonthly : false,
                        })
                      }
                      className="h-auto w-auto px-2 py-1.5"
                    >
                      <option value="week_default">Every week</option>
                      <option value="month">Monthly</option>
                    </NativeSelect>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {isWeekly ? (
                      <input
                        type="checkbox"
                        checked={config.inferMtdFromWeekly}
                        onChange={(event) =>
                          updateConfig(metric, { inferMtdFromWeekly: event.target.checked })
                        }
                        aria-label={`${metric.label} infer MTD from weekly`}
                      />
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {isWeekly ? (
                      <input
                        type="checkbox"
                        checked={config.inferQtdFromWeekly}
                        onChange={(event) =>
                          updateConfig(metric, { inferQtdFromWeekly: event.target.checked })
                        }
                        aria-label={`${metric.label} infer QTD from weekly`}
                      />
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {!isWeekly ? (
                      <input
                        type="checkbox"
                        checked={config.inferQtdFromMonthly}
                        onChange={(event) =>
                          updateConfig(metric, { inferQtdFromMonthly: event.target.checked })
                        }
                        aria-label={`${metric.label} infer QTD from monthly`}
                      />
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs text-[var(--color-text-muted)]">
        {saveState === "saving" ? "Saving rules…" : null}
        {saveState === "saved" ? "Rules saved." : null}
        {saveState === "error" ? "Failed to save rules." : null}
      </p>
    </section>
  );
}
